import React from "react"
import {Box, Tooltip, Typography} from "@mui/material"

import {CopyButton} from "@indebted/components/CopyButton"
import {EmailStatusIcon} from "@indebted/components/EmailStatusIcon"
import type {ContactConsent, CustomerStatus} from "@indebted/api"

const contactDateOptions = {year: "numeric", month: "short", day: "numeric"} as const

function formatContactDate(value: string) {
	const parsed = new Date(value)
	if (Number.isNaN(parsed.getTime())) return value
	return new Intl.DateTimeFormat("default", contactDateOptions).format(parsed)
}

function removedTooltipText(removedAt: string, removedSource?: "agent" | "customer") {
	const when = formatContactDate(removedAt)
	// Only customer-initiated removals are called out explicitly; agent-initiated
	// (and any unspecified source) intentionally fall back to the neutral wording.
	return removedSource === "customer" ? `Removed by customer on ${when}` : `Removed on ${when}`
}

const CONSENT_GRANTED_ICON = "👍"
const CONSENT_REVOKED_ICON = "❌"

function consentIcon(consent: ContactConsent) {
	return consent.Revoked ? CONSENT_REVOKED_ICON : CONSENT_GRANTED_ICON
}

function consentTooltipText(consent: ContactConsent) {
	const decision = consent.Revoked ? "Consent revoked" : "Consent granted"
	const who = consent.Source === "customer" ? " by customer" : ""
	return `${decision}${who} on ${formatContactDate(consent.DecidedAt)}`
}

// Reads the frontend rollout flag. Kept here rather than inline in Overview so the
// flag value is unit-testable without importing Overview's heavy module graph.
function removedContactsUiEnabled(): boolean {
	return import.meta.env.VITE_ENABLE_REMOVED_CONTACTS_UI === "true"
}

// When the removed-contacts UI flag is off, drop removed rows so the page renders exactly as before.
function visibleContacts<T extends {RemovedAt?: string}>(items: T[], showRemoved: boolean): T[] {
	return showRemoved ? items : items.filter((item) => !item.RemovedAt)
}

function ContactValueDisplay({
	value,
	status,
	removedAt,
	removedSource,
	consent,
	consentButtons,
	deleteButton,
}: {
	value: string
	status: CustomerStatus
	removedAt?: string
	removedSource?: "agent" | "customer"
	consent?: ContactConsent
	consentButtons?: React.ReactNode
	deleteButton?: React.ReactNode
}) {
	// Removed contacts are shown for awareness only — struck through, with no copy
	// or delete affordance — so agents stop reaching out on them.
	if (removedAt) {
		return (
			<Typography variant="body2">
				<Tooltip title={removedTooltipText(removedAt, removedSource)} placement="top-start">
					<Box component="span" color="text.disabled" style={{textDecoration: "line-through"}}>
						{value}
					</Box>
				</Tooltip>
			</Typography>
		)
	}

	return (
		<>
			<Typography variant="body2">
				<CopyButton content={value} />
				{value}
				<EmailStatusIcon title={status.Description} icon={status.Icon} />
				{consent && <EmailStatusIcon title={consentTooltipText(consent)} icon={consentIcon(consent)} />}
			</Typography>
			{/* The gap keeps the action buttons apart so a slip doesn't land on the neighbour. */}
			<Box component="span" display="flex" alignItems="center" gap={1.5}>
				{consentButtons}
				{deleteButton}
			</Box>
		</>
	)
}

export {ContactValueDisplay, visibleContacts, removedContactsUiEnabled}
